import { DefaultValues, FieldValues, useForm, UseFormProps } from '@slal/ui/react-hook-form/core';
import { attempt, get, isError } from 'lodash';
import { useCallback, useContext, useEffect } from 'react';
import { FormDataContext } from '~/providers';
import { FormDataContextValue, FormDataType, FormKeys } from '~/types';

export const FORM_KEY = 'FORM';

export const useFormSession = <FormValues>() => {
    const setSession = useCallback((values: object) => {
        const formSession = sessionStorage.getItem(FORM_KEY);
        const formData: FormDataType = formSession ? attempt(JSON.parse, formSession) : {};

        if (isError(formData)) {
            console.error('Error parsing JSON from session storage');
            return;
        }

        const updatedForm = { ...formData, ...values };
        sessionStorage.setItem(FORM_KEY, JSON.stringify(updatedForm));
    }, []);

    const getSession = useCallback(
        (formKey: FormKeys): (FormDataType & DefaultValues<FormValues> & FormValues) | undefined => {
            const formSession = sessionStorage.getItem(FORM_KEY);

            const formData: FormDataType = formSession ? attempt(JSON.parse, formSession) : {};

            if (isError(formData)) {
                console.error('Error parsing JSON from session storage');
                return undefined;
            }

            const formValues = get(formData, formKey);
            if (!formValues) return undefined;

            return {
                [formKey]: formValues,
            } as (FormDataType & DefaultValues<FormValues> & FormValues) | undefined;
        },
        []
    );

    return {
        setSession,
        getSession,
    };
};

export const useFormHandler = <FormValues extends FieldValues = FieldValues>(
    options: UseFormProps<FormValues> & { saveToSession?: boolean },
    formKey: FormKeys
) => {
    const saveToSession = options.saveToSession !== false;
    const { getSession } = useFormSession<FormValues>();
    const { updateFormDataState } = useFormDataContext();

    useEffect(() => {
        const initialValuesFromStorage = getSession(formKey);

        if (!initialValuesFromStorage && saveToSession) {
            updateFormDataState(options.defaultValues as FormDataType);
        }
    }, [saveToSession, getSession, formKey, updateFormDataState, options.defaultValues]);

    const baseFormOptions: UseFormProps<FormValues> = {
        mode: 'onTouched' as const,
        resetOptions: {
            keepDirty: false,
            keepErrors: false,
            keepTouched: true,
        },
    };
    const sessionValues = getSession(formKey) || options.defaultValues;

    const form = useForm({ ...baseFormOptions, ...options, defaultValues: sessionValues });

    const handleSubmit = useCallback(
        (onValidCallback: () => void) => async (event: { preventDefault: () => void }) => {
            event.preventDefault();
            const valid = await form.trigger();
            const formValues: FormValues = form.getValues();
            updateFormDataState(formValues);
            if (!valid) {
                return;
            }
            if (onValidCallback) onValidCallback();
        },
        [updateFormDataState, form]
    );
    return {
        form,
        handleSubmit,
    };
};

export const useFormDataContext = (): FormDataContextValue => {
    const context = useContext(FormDataContext);
    if (!context) {
        throw new Error('useFormDataContext must be used within an FormDataContextProvider');
    }
    return context;
};
